// Foresight Studio (obj_type "F") — the Analytics ML workspace: time-series
// forecasting + predict-a-column experiments over the project's data.
import api from './client'

export type ForesightTask = 'forecast' | 'predict'

export interface ForesightSeries {
  DATE_COLUMN?: string
  BUCKET?: 'day' | 'week' | 'month' | 'quarter' | 'year'
  MEASURE?: string
  AGG?: 'SUM' | 'AVG' | 'COUNT' | 'MIN' | 'MAX'
  FREQUENCY?: string
}

export interface ForesightModel {
  FORESIGHT: {
    NAME: string
    DESCRIPTION?: string
    TASK: ForesightTask
    SOURCE: { MODE: 'table' | 'sql'; TABLE?: string; SQL?: string; CRITERIA?: string }
    SERIES?: ForesightSeries
    TARGET?: { COLUMN?: string; FEATURES?: string[]; TASK_TYPE?: string }
    PARAMS?: {
      HORIZON?: number
      CONFIDENCE?: number
      BACKTEST?: boolean
      HOLDOUT_FRACTION?: number
    }
    OUTPUT?: { TABLE?: string }
  }
}

export interface ForesightRunSummary {
  run_id: string
  task: ForesightTask
  status: 'running' | 'completed' | 'failed' | 'cancelled'
  started_at?: string
  finished_at?: string
  metrics?: Record<string, number | string | null>
  engine?: string
  error?: string
  champion?: boolean
}

export interface ForesightRun extends ForesightRunSummary {
  progress?: number
  poll_error?: string
  result?: {
    // forecast
    frequency?: string
    horizon?: number
    confidence?: number
    history?: { timestamps: string[]; values: number[] }
    forecast?: { timestamps: string[]; yhat: number[]; lo: number[]; hi: number[] }
    backtest?: { mape?: number; mae?: number } | null
    narrative?: Record<string, unknown>
    // predict
    task?: string
    target?: string
    drivers?: { feature: string; importance: number }[]
    scored_count?: number
    trained_count?: number
    scored_rows?: Record<string, unknown>[]
  }
  materialized?: { table: string; rows: number; at: string }[]
}

export interface ForesightSummary {
  obj_name: string
  name: string
  description: string
  task: ForesightTask
  updated_at: string
  last_run: ForesightRunSummary | null
}

export interface ForesightColumn {
  name: string
  type: 'date' | 'number' | 'string' | 'boolean'
  db_type: string
}

export async function listForesight(project: string): Promise<ForesightSummary[]> {
  const { data } = await api.get('/foresight/list', { params: { project } })
  return data.models || []
}

export async function getForesight(project: string, objName: string): Promise<ForesightModel> {
  const { data } = await api.get('/foresight/model', { params: { project, obj_name: objName } })
  return data.model
}

export async function saveForesight(
  project: string, objName: string, model: ForesightModel,
): Promise<void> {
  await api.post('/foresight/save', { project, obj_name: objName, model })
}

export async function deleteForesight(project: string, objName: string): Promise<void> {
  await api.delete('/foresight/delete', { params: { project, obj_name: objName } })
}

export async function listForesightTables(project: string): Promise<string[]> {
  const { data } = await api.get('/foresight/tables', { params: { project } })
  return data.tables || []
}

export async function listForesightColumns(
  project: string, table: string,
): Promise<ForesightColumn[]> {
  const { data } = await api.get('/foresight/columns', { params: { project, table } })
  return data.columns || []
}

export async function previewForesightSource(
  project: string, model: ForesightModel,
): Promise<{ columns: string[]; rows: Record<string, unknown>[] }> {
  const { data } = await api.post('/foresight/preview-source', { project, model })
  return data.preview
}

export async function runForesight(project: string, objName: string): Promise<ForesightRun> {
  const { data } = await api.post('/foresight/run', { project, obj_name: objName })
  return data.run
}

export async function listForesightRuns(
  project: string, objName: string,
): Promise<ForesightRunSummary[]> {
  const { data } = await api.get('/foresight/runs', { params: { project, obj_name: objName } })
  return data.runs || []
}

export async function getForesightRun(
  project: string, objName: string, runId: string,
): Promise<ForesightRun> {
  const { data } = await api.get('/foresight/run', {
    params: { project, obj_name: objName, run_id: runId },
  })
  return data.run
}

export async function materializeForesightRun(
  project: string, objName: string, runId: string,
): Promise<{ table: string; rows: number }> {
  const { data } = await api.post('/foresight/materialize', {
    project, obj_name: objName, run_id: runId,
  })
  return { table: data.table, rows: data.rows }
}
